import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Box, ClickAwayListener, InputAdornment, List, ListItemButton, ListItemText,
  Paper, Popper, TextField, Typography,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import SortableModelList from '../evolution/SortableModelList';

export const smallTemplateChipSx = {
  height: 22,
  maxWidth: '100%',
  fontSize: '0.68rem',
  fontWeight: 600,
  '& .MuiChip-label': { px: 1, whiteSpace: 'normal' as const, lineHeight: 1.25 },
};

export const orderPrescriptionTemplates = (list: any[], mode: 'default' | 'alpha' = 'default') => {
  const copy = [...list];
  if (mode === 'alpha') {
    return copy.sort((a, b) =>
      String(a.name || '').localeCompare(String(b.name || ''), 'pt-BR', { sensitivity: 'base' }),
    );
  }
  return copy.sort((a, b) => {
    const sa = Number(a.sort_order ?? 0);
    const sb = Number(b.sort_order ?? 0);
    if (sa !== sb) return sa - sb;
    return String(a.name || '').localeCompare(String(b.name || ''), 'pt-BR', { sensitivity: 'base' });
  });
};

export const filterTemplatesByTitle = (list: any[], query: string) => {
  const q = query.trim().toLowerCase();
  if (!q) return list;
  return list.filter((t) => String(t.name || '').toLowerCase().includes(q));
};

export interface PrescriptionTemplateChipPickerProps {
  templates: any[];
  onSelect: (template: any) => void;
  onReorder?: (orderedIds: string[]) => void | Promise<void>;
  title?: string;
  headerExtra?: React.ReactNode;
  footerExtra?: React.ReactNode;
  searchPlaceholder?: string;
  emptySearchMessage?: (query: string) => string;
  /** Layout enxuto dentro da evolução em notebook. */
  dense?: boolean;
}

/** Campo de pesquisa com lista suspensa filtrável e reordenável por arrastar. */
const PrescriptionTemplateChipPicker: React.FC<PrescriptionTemplateChipPickerProps> = ({
  templates,
  onSelect,
  onReorder,
  title = 'Modelos pré-preenchidos',
  headerExtra,
  footerExtra,
  searchPlaceholder = 'Pesquisar por título...',
  emptySearchMessage = (q) => `Nenhum modelo encontrado para "${q}".`,
  dense = false,
}) => {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [popperWidth, setPopperWidth] = useState<number | undefined>();

  useLayoutEffect(() => {
    if (open && anchorRef.current) setPopperWidth(anchorRef.current.offsetWidth);
  }, [open]);

  const ordered = useMemo(() => orderPrescriptionTemplates(templates), [templates]);
  const filtered = useMemo(() => filterTemplatesByTitle(ordered, search), [ordered, search]);
  const isSearching = search.trim().length > 0;

  const handleSelect = (template: any) => {
    onSelect(template);
    setSearch('');
    setOpen(false);
  };

  const handleReorder = (orderedIds: string[]) => {
    onReorder?.(orderedIds);
  };

  const templateById = useMemo(() => new Map(ordered.map((t) => [String(t.id), t])), [ordered]);

  if (templates.length === 0) return null;

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box sx={{ mb: dense ? 0.75 : 2.5, position: 'relative' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: dense ? 0.35 : 1, flexWrap: 'wrap' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0284c7', fontSize: dense ? '0.8rem' : undefined }}>
            {title}
          </Typography>
          {headerExtra}
        </Box>

        <Box ref={anchorRef}>
          <TextField
            fullWidth
            size="small"
            placeholder={searchPlaceholder}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onClick={() => setOpen(true)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#94a3b8', fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Popper
          open={open}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          disablePortal
          sx={{ zIndex: 10, width: popperWidth }}
          modifiers={[{ name: 'offset', options: { offset: [0, 4] } }]}
        >
          <Paper
            elevation={4}
            sx={{
              width: popperWidth,
              maxHeight: 320,
              overflow: 'auto',
              borderRadius: 2,
              border: '1px solid #e2e8f0',
            }}
          >
            {!isSearching && onReorder && (
              <Typography variant="caption" sx={{ display: 'block', px: 1.5, pt: 1.25, pb: 0.5, color: '#64748b' }}>
                Clique para aplicar · arraste para reordenar (do mais ao menos comum)
              </Typography>
            )}

            {!isSearching && onReorder ? (
              <Box sx={{ px: 1, pb: footerExtra ? 0 : 1 }}>
                <SortableModelList
                  items={ordered.map((t) => ({ id: String(t.id), name: t.name || 'Modelo' }))}
                  showIndex
                  onReorder={handleReorder}
                  onSelect={(item) => {
                    const tpl = templateById.get(item.id);
                    if (tpl) handleSelect(tpl);
                  }}
                />
              </Box>
            ) : filtered.length === 0 ? (
              <Typography variant="body2" sx={{ color: '#94a3b8', px: 2, py: 1.5 }}>
                {emptySearchMessage(search.trim())}
              </Typography>
            ) : (
              <List dense disablePadding sx={{ py: 0.5 }}>
                {filtered.map((tpl: any, idx: number) => (
                  <ListItemButton
                    key={tpl.id}
                    onClick={() => handleSelect(tpl)}
                    sx={{
                      py: 0.75,
                      '&:hover': { bgcolor: '#f0f9ff' },
                    }}
                  >
                    <ListItemText
                      primary={`${idx + 1}. ${tpl.name}`}
                      primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 600, color: '#334155' }}
                    />
                  </ListItemButton>
                ))}
              </List>
            )}

            {footerExtra && (
              <Box sx={{ px: 1.5, py: 1, borderTop: '1px solid #e5e7eb', bgcolor: '#f8fafc' }}>
                {footerExtra}
              </Box>
            )}
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

export default PrescriptionTemplateChipPicker;
